import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import { useTranslation } from "react-i18next";
import { useLanguageAwareUrl } from "../../utils/languageAwareUrl";
import { auth } from "../../config/firebase";
import {
  Home,
  Compass,
  Map,
  Menu,
  X,
  Moon,
  Sun,
  User,
  LogOut,
  MessageCircle,
  HelpCircle,
  GitBranch,
  ChevronDown,
  MoreHorizontal,
  LayoutDashboard,
  Settings,
} from "lucide-react";
import LanguageSwitcher from "../LanguageSwitcher";

const NavContainer = styled.nav`
  background: var(--surface-color);
  box-shadow: 0 2px 10px var(--shadow-color);
  padding: 0 1rem;
  position: sticky;
  top: 0;
  z-index: 1000;
  border-bottom: 1px solid var(--border-color);
  transition: background-color 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    border-color 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  /* Big TVs */
  @media (min-width: 1920px) {
    padding: 0 3rem;
  }

  /* Desktop */
  @media (min-width: 1024px) and (max-width: 1919px) {
    padding: 0 2rem;
  }

  /* iPad range */
  @media (min-width: 768px) and (max-width: 1023px) {
    padding: 0 1.5rem;
  }

  /* Small tablets and phones */
  @media (max-width: 767px) {
    padding: 0 1rem;
  }
`;

const NavContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 70px;
  gap: 1rem;

  /* Big TVs - increase max-width */
  @media (min-width: 1920px) {
    max-width: 1600px;
    gap: 1.5rem;
  }

  /* Desktop */
  @media (min-width: 1024px) and (max-width: 1919px) {
    max-width: 1200px;
    gap: 1rem;
  }

  /* iPad range */
  @media (min-width: 768px) and (max-width: 1023px) {
    max-width: 100%;
    gap: 0.75rem;
  }

  /* Small tablets */
  @media (min-width: 600px) and (max-width: 767px) {
    gap: 0.5rem;
  }

  /* Small phones */
  @media (max-width: 599px) {
    gap: 0.5rem;
  }
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  color: var(--text-color);
  font-size: 1.2rem;
  font-weight: 700;
  flex-shrink: 1;
  min-width: 0;

  /* Big TVs */
  @media (min-width: 1920px) {
    font-size: 1.8rem;
    gap: 0.75rem;
  }

  /* Desktop */
  @media (min-width: 1024px) and (max-width: 1919px) {
    font-size: 1.5rem;
  }

  /* iPad range - full logo */
  @media (min-width: 768px) and (max-width: 1023px) {
    font-size: 1.3rem;
    gap: 0.5rem;
  }

  /* Small tablets */
  @media (min-width: 600px) and (max-width: 767px) {
    font-size: 1rem;
    gap: 0.4rem;
  }

  /* Small phones */
  @media (max-width: 599px) {
    font-size: 0.85rem;
    gap: 0.25rem;
  }

  @media (max-width: 400px) {
    font-size: 0.75rem;
    gap: 0.2rem;
  }

  svg {
    /* Big TVs */
    @media (min-width: 1920px) {
      width: 32px;
      height: 32px;
    }

    /* Desktop */
    @media (min-width: 1024px) and (max-width: 1919px) {
      width: 28px;
      height: 28px;
    }

    /* iPad range */
    @media (min-width: 768px) and (max-width: 1023px) {
      width: 24px;
      height: 24px;
    }

    /* Small tablets */
    @media (min-width: 600px) and (max-width: 767px) {
      width: 22px;
      height: 22px;
    }

    /* Small phones */
    @media (max-width: 599px) {
      width: 20px;
      height: 20px;
    }

    @media (max-width: 400px) {
      width: 18px;
      height: 18px;
    }
  }
`;

const NavLinks = styled.div<{ $isOpen: boolean }>`
  display: flex;
  align-items: center;
  gap: 1.5rem;

  /* Big TVs */
  @media (min-width: 1920px) {
    gap: 2rem;
  }

  /* Desktop */
  @media (min-width: 1024px) and (max-width: 1919px) {
    gap: 1.5rem;
  }

  /* iPad range - full navigation */
  @media (min-width: 768px) and (max-width: 1023px) {
    gap: 1rem;
  }

  /* Small tablets - tighter spacing for icons */
  @media (min-width: 600px) and (max-width: 767px) {
    gap: 0.5rem;
  }

  /* Small phones - mobile menu */
  @media (max-width: 599px) {
    position: fixed;
    top: 70px;
    left: 0;
    right: 0;
    background: var(--surface-color);
    flex-direction: column;
    padding: 2rem;
    box-shadow: 0 8px 32px var(--shadow-color);
    transform: translateY(${(props) => (props.$isOpen ? "0" : "-100%")});
    opacity: ${(props) => (props.$isOpen ? "1" : "0")};
    visibility: ${(props) => (props.$isOpen ? "visible" : "hidden")};
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    gap: 1rem;
    z-index: 1001;
    border-bottom: 1px solid var(--border-color);
  }
`;

const NavLink = styled(Link)<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  color: ${(props) =>
    props.$active ? "var(--primary-color)" : "var(--text-color)"};
  font-weight: ${(props) => (props.$active ? "600" : "500")};
  padding: 0.5rem 1rem;
  border-radius: 6px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;

  &:hover {
    background: ${(props) =>
      props.$active ? "var(--primary-color)" : "var(--background-color)"};
    color: ${(props) => (props.$active ? "white" : "var(--primary-color)")};
    opacity: ${(props) => (props.$active ? "1" : "0.8")};
  }

  /* Big TVs - full navigation */
  @media (min-width: 1920px) {
    padding: 0.6rem 1.2rem;
    font-size: 1.1rem;
  }

  /* Desktop - full navigation */
  @media (min-width: 1024px) and (max-width: 1919px) {
    padding: 0.5rem 1rem;
  }

  /* iPad range - show full text */
  @media (min-width: 900px) and (max-width: 1023px) {
    padding: 0.5rem 0.75rem;
    font-size: 0.95rem;
    gap: 0.5rem;
  }

  /* Medium tablets - icons only to prevent overlap */
  @media (min-width: 600px) and (max-width: 899px) {
    padding: 0.5rem;
    gap: 0;

    .nav-link-text {
      display: none;
    }
  }

  /* Small phones - mobile menu */
  @media (max-width: 599px) {
    width: 100%;
    justify-content: center;
    padding: 0.75rem 1rem;
    font-size: 1rem;
    color: ${(props) =>
      props.$active ? "var(--primary-color)" : "var(--text-color)"};
    font-weight: ${(props) => (props.$active ? "600" : "500")};
    border-radius: 6px;
    background: transparent;
    border: none;
    transition: all 0.2s ease;

    &:hover {
      background: var(--background-color);
      color: var(--primary-color);
      transform: none;
      box-shadow: none;
    }

    &:active {
      transform: none;
    }
  }
`;

const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;

  @media (max-width: 599px) {
    width: 100%;
    display: block;
  }
`;

const DropdownButton = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  color: ${(props) =>
    props.$active ? "var(--primary-color)" : "var(--text-color)"};
  font-weight: ${(props) => (props.$active ? "600" : "500")};
  padding: 0.5rem 1rem;
  border-radius: 6px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;
  cursor: pointer;
  font-size: inherit;

  &:hover {
    background: var(--background-color);
    color: var(--primary-color);
  }

  /* Big TVs */
  @media (min-width: 1920px) {
    padding: 0.6rem 1.2rem;
    font-size: 1.1rem;
  }

  /* Desktop */
  @media (min-width: 1024px) and (max-width: 1919px) {
    padding: 0.5rem 1rem;
  }

  /* iPad range - full text */
  @media (min-width: 900px) and (max-width: 1023px) {
    padding: 0.5rem 0.75rem;
    font-size: 0.95rem;
    gap: 0.5rem;
  }

  /* Medium tablets - icons only to prevent overlap */
  @media (min-width: 600px) and (max-width: 899px) {
    padding: 0.5rem;
    gap: 0;

    .nav-link-text {
      display: none;
    }

    .desktop-only {
      display: none;
    }
  }

  /* Small phones - mobile menu */
  @media (max-width: 599px) {
    width: 100%;
    justify-content: center;
    padding: 0.75rem 1rem;
    font-size: 1rem;
    border-radius: 6px;
    background: transparent;
    border: none;
    transition: all 0.2s ease;
    box-sizing: border-box;
    text-align: center;

    &:hover {
      background: var(--background-color);
      color: var(--primary-color);
      transform: none;
      box-shadow: none;
    }

    &:active {
      transform: none;
    }

    .desktop-only {
      display: block;
    }
  }
`;

const DropdownMenu = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  background: var(--surface-color);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  min-width: 200px;
  z-index: 1000;
  opacity: ${(props) => (props.$isOpen ? "1" : "0")};
  visibility: ${(props) => (props.$isOpen ? "visible" : "hidden")};
  transform: translateY(${(props) => (props.$isOpen ? "0" : "-10px")});
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  @media (max-width: 599px) {
    position: static;
    box-shadow: none;
    border: none;
    margin-top: 0;
    opacity: 1;
    visibility: visible;
    transform: none;
    display: ${(props) => (props.$isOpen ? "block" : "none")};
    background: transparent;
    width: 100%;
  }
`;

const DropdownItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  text-decoration: none;
  color: var(--text-color);
  transition: background-color 0.3s ease;

  &:hover {
    background: var(--background-color);
    color: var(--primary-color);
  }

  &:first-child {
    border-radius: 8px 8px 0 0;
  }

  &:last-child {
    border-radius: 0 0 8px 8px;
  }

  @media (max-width: 599px) {
    width: 100%;
    padding: 0.75rem 1rem;
    border-radius: 6px;
    margin: 0.25rem 0;
    background: transparent;
    border: none;
    font-size: 1rem;
    font-weight: 500;
    transition: all 0.2s ease;
    box-sizing: border-box;
    justify-content: center;

    &:hover {
      background: var(--background-color);
      color: var(--primary-color);
      transform: none;
      box-shadow: none;
    }

    &:active {
      transform: none;
    }
  }
`;

const UserSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-shrink: 0;

  /* Big TVs */
  @media (min-width: 1920px) {
    gap: 1.5rem;
  }

  /* Desktop */
  @media (min-width: 1024px) and (max-width: 1919px) {
    gap: 1rem;
  }

  /* iPad range */
  @media (min-width: 768px) and (max-width: 1023px) {
    gap: 0.75rem;
  }

  /* Small tablets */
  @media (min-width: 600px) and (max-width: 767px) {
    gap: 0.5rem;
  }

  /* Small phones */
  @media (max-width: 599px) {
    gap: 0.5rem;
  }
`;

const ThemeToggle = styled.button`
  background: none;
  border: none;
  color: var(--text-color);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 6px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  min-height: 44px;

  &:hover {
    background: var(--background-color);
    color: var(--primary-color);
  }

  @media (max-width: 599px) {
    min-width: 36px;
    min-height: 36px;
    padding: 0.3rem;
  }
`;

const UserAvatar = styled.div<{ avatar?: string }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${(props) =>
    props.avatar ? `url(${props.avatar})` : "var(--primary-color)"};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.05);
  }
`;

const UserMenu = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  background: var(--surface-color);
  border-radius: 8px;
  box-shadow: 0 8px 24px var(--shadow-color);
  min-width: 200px;
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  visibility: ${(props) => (props.$isOpen ? "visible" : "hidden")};
  transform: translateY(${(props) => (props.$isOpen ? "0" : "-10px")});
  transition: all 0.2s;
  z-index: 1000;
  border: 1px solid var(--border-color);

  @media (max-width: 767px) {
    right: 1rem;
    min-width: 180px;
  }
`;

const UserMenuItem = styled.button`
  width: 100%;
  padding: 0.75rem 1rem;
  background: none;
  border: none;
  text-align: left;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-color);
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: var(--background-color);
  }

  &:first-child {
    border-radius: 8px 8px 0 0;
  }

  &:last-child {
    border-radius: 0 0 8px 8px;
    border-top: 1px solid var(--border-color);
    color: var(--error-color);
  }
`;

const UserMenuContainer = styled.div`
  position: relative;
`;

const AuthButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

  @media (max-width: 900px) {
    gap: 0.75rem;
  }

  @media (max-width: 850px) {
    gap: 0.5rem;
  }

  @media (max-width: 599px) {
    gap: 0.5rem;
  }
`;

const AuthButton = styled(Link)`
  padding: 0.5rem 1rem;
  border-radius: 6px;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: inherit;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 900px) {
    padding: 0.5rem 0.75rem;
    font-size: 0.95rem;
  }

  @media (max-width: 850px) {
    padding: 0.4rem 0.6rem;
    font-size: 0.9rem;
  }

  @media (max-width: 599px) {
    padding: 0.5rem 0.75rem;
    font-size: 0.95rem;
  }

  &.primary {
    background: var(--primary-color);
    color: white;

    &:hover {
      background: var(--primary-color);
      opacity: 0.9;
    }

    @media (max-width: 599px) {
      background: transparent;
      color: var(--primary-color);
      border: 2px solid var(--primary-color);

      &:hover {
        background: var(--primary-color);
        color: white;
      }
    }
  }

  &.secondary {
    background: transparent;
    color: var(--primary-color);
    border: 2px solid var(--primary-color);

    &:hover {
      background: var(--primary-color);
      color: white;
    }

    @media (max-width: 599px) {
      display: none;
    }
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: var(--text-color);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 6px;
  transition: background-color 0.2s;
  min-width: 44px;
  min-height: 44px;

  &:hover {
    background: var(--background-color);
  }

  /* Show mobile menu button only on small phones */
  @media (max-width: 599px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const Overlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 70px;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--overlay-color);
  opacity: ${(props) => (props.$isOpen ? "1" : "0")};
  visibility: ${(props) => (props.$isOpen ? "visible" : "hidden")};
  transition: all 0.3s ease;
  z-index: 1000;

  /* Hide overlay on tablets and larger */
  @media (min-width: 600px) {
    display: none;
  }
`;

const Navigation: React.FC = () => {
  const { user, logout } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const { t } = useTranslation();
  const { buildUrl } = useLanguageAwareUrl();

  // Check if user is using email/password authentication (not Google SSO)
  const [isPasswordAuth, setIsPasswordAuth] = useState(false);

  useEffect(() => {
    const checkAuthProvider = async () => {
      const currentUser = auth.currentUser;
      if (currentUser) {
        // Check if user has password provider (email/password authentication)
        const hasPasswordProvider = currentUser.providerData.some(
          (provider) => provider.providerId === "password"
        );
        setIsPasswordAuth(hasPasswordProvider);
      }
    };

    checkAuthProvider();
  }, [user]);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const userMenuRef = React.useRef<HTMLDivElement>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  // Handle click outside user menu and dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target as Node)
      ) {
        setUserMenuOpen(false);
      }
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };

    if (userMenuOpen || dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [userMenuOpen, dropdownOpen]);

  const handleLogout = async () => {
    try {
      await logout();
      setUserMenuOpen(false);
      setMobileMenuOpen(false);
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const isActive = (path: string) => {
    const currentPath = location.pathname;

    // Handle homepage special case (both / and /fa)
    if (path === "/") {
      return currentPath === "/" || currentPath === "/fa";
    }

    // Handle FAQ special case (both /faq and /fa/faq)
    if (path === "/faq") {
      return currentPath === "/faq" || currentPath === "/fa/faq";
    }

    // For other paths, check exact match
    return currentPath === path;
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setDropdownOpen(false);
  };

  return (
    <NavContainer>
      <NavContent>
        <Logo to={buildUrl("/")} onClick={closeMobileMenu}>
          <Map size={24} />
          <span>NewME.social</span>
        </Logo>

        <NavLinks $isOpen={mobileMenuOpen}>
          <NavLink
            to={buildUrl("/")}
            $active={isActive("/") || isActive("/fa")}
            onClick={closeMobileMenu}
          >
            <Home size={20} />
            <span className="nav-link-text">{t("navigation.home")}</span>
          </NavLink>
          <NavLink
            to={buildUrl("/explore")}
            $active={isActive("/explore")}
            onClick={closeMobileMenu}
          >
            <Compass size={20} />
            <span className="nav-link-text">{t("navigation.explore")}</span>
          </NavLink>
          {user && (
            <NavLink
              to={buildUrl("/dashboard")}
              $active={isActive("/dashboard")}
              onClick={closeMobileMenu}
            >
              <LayoutDashboard size={20} />
              <span className="nav-link-text">{t("navigation.dashboard")}</span>
            </NavLink>
          )}
          <DropdownContainer ref={dropdownRef}>
            <DropdownButton
              $active={
                isActive("/contact") ||
                isActive("/faq") ||
                isActive("/changelog")
              }
              onClick={() => setDropdownOpen(!dropdownOpen)}
            >
              <MoreHorizontal size={20} />
              <span className="nav-link-text">{t("navigation.more")}</span>
              <ChevronDown
                size={16}
                style={{
                  transform: dropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.3s ease",
                }}
                className="desktop-only"
              />
            </DropdownButton>
            <DropdownMenu $isOpen={dropdownOpen}>
              <DropdownItem
                to={buildUrl("/contact")}
                onClick={() => {
                  setDropdownOpen(false);
                  closeMobileMenu();
                }}
              >
                <MessageCircle size={16} />
                {t("navigation.contact")}
              </DropdownItem>
              <DropdownItem
                to={buildUrl("/faq")}
                onClick={() => {
                  setDropdownOpen(false);
                  closeMobileMenu();
                }}
              >
                <HelpCircle size={16} />
                {t("navigation.faq")}
              </DropdownItem>
              <DropdownItem
                to={buildUrl("/changelog")}
                onClick={() => {
                  setDropdownOpen(false);
                  closeMobileMenu();
                }}
              >
                <GitBranch size={16} />
                {t("navigation.changelog")}
              </DropdownItem>
            </DropdownMenu>
          </DropdownContainer>
        </NavLinks>

        <UserSection>
          <LanguageSwitcher />

          <ThemeToggle
            onClick={toggleTheme}
            title={
              isDarkMode
                ? t("theme.switchToLightMode")
                : t("theme.switchToDarkMode")
            }
          >
            {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
          </ThemeToggle>

          {user ? (
            <>
              <UserMenuContainer ref={userMenuRef}>
                <UserAvatar
                  avatar={user.avatar}
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                >
                  {!user.avatar && user.displayName.charAt(0).toUpperCase()}
                </UserAvatar>

                <UserMenu $isOpen={userMenuOpen}>
                  <UserMenuItem
                    onClick={() => {
                      setUserMenuOpen(false);
                      navigate(buildUrl("/profile"));
                    }}
                  >
                    <User size={16} />
                    {t("navigation.profile")}
                  </UserMenuItem>
                  {isPasswordAuth && (
                    <UserMenuItem
                      onClick={() => {
                        setUserMenuOpen(false);
                        navigate("/settings");
                      }}
                    >
                      <Settings size={16} />
                      Settings
                    </UserMenuItem>
                  )}
                  <UserMenuItem onClick={handleLogout}>
                    <LogOut size={16} />
                    {t("navigation.logout")}
                  </UserMenuItem>
                </UserMenu>
              </UserMenuContainer>
            </>
          ) : (
            <AuthButtons>
              <AuthButton to="/login" className="primary">
                {t("navigation.login")}
              </AuthButton>
            </AuthButtons>
          )}

          <MobileMenuButton onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </MobileMenuButton>
        </UserSection>
      </NavContent>

      <Overlay $isOpen={mobileMenuOpen} onClick={closeMobileMenu} />
    </NavContainer>
  );
};

export default Navigation;
